import { useMemo } from 'react';

import {
    AssetClass,
    AssetType,
    InferredPosition,
    PortfolioInferredPositions,
} from '@/cip/_openapi/portfolio-optimizer';
import { useAssetReplacements } from '@/cip/api/asset';

import { TLHHolding, TLHHoldingCategory } from './tlh-types';

export const useTlhCandidates = (inferredPositions: PortfolioInferredPositions[]) => {
    const strategyAssets = useMemo(() => collectStrategyAssets(inferredPositions), [inferredPositions]);
    const scIds = useMemo(() => strategyAssets.map((asset) => asset.scId), [strategyAssets]);
    const { data: replacementsByScId = {}, isLoading } = useAssetReplacements(scIds);

    return useMemo<{ data: TLHHolding[]; isLoading: boolean }>(
        () => ({
            data: strategyAssets.map((asset) => ({
                ...asset,
                suggestedReplacements: (replacementsByScId[asset.scId] ?? []).flatMap((replacement) =>
                    replacement.scId
                        ? [
                              {
                                  sId: replacement.sId,
                                  scId: replacement.scId,
                                  ticker: replacement.symbol ?? '—',
                                  name: replacement.description ?? replacement.symbol ?? replacement.sId,
                              },
                          ]
                        : [],
                ),
            })),
            isLoading,
        }),
        [isLoading, replacementsByScId, strategyAssets],
    );
};

const collectStrategyAssets = (inferredPositions: PortfolioInferredPositions[]): TLHHolding[] => {
    const assetsByScId = new Map<string, TLHHolding>();

    inferredPositions.forEach((portfolio) => {
        portfolio.positions.forEach((position) => addStrategyAsset(assetsByScId, position));
    });

    return [...assetsByScId.values()].sort((a, b) => b.value - a.value);
};

const addStrategyAsset = (assetsByScId: Map<string, TLHHolding>, position: InferredPosition) => {
    if (!position.scId) return;

    const existing = assetsByScId.get(position.scId);
    if (existing) {
        existing.value += position.currentExposure;
        return;
    }

    assetsByScId.set(position.scId, {
        sId: position.sId,
        scId: position.scId,
        ticker: position.symbol ?? '—',
        name: position.description,
        value: position.currentExposure,
        category: categoryForPosition(position),
        suggestedReplacements: [],
    });
};

const STOCK_ASSET_TYPES: AssetType[] = [
    AssetType.ADR,
    AssetType.COMMON_STOCK,
    AssetType.PREFERRED,
    AssetType.REORGANIZED_STOCK,
];

const categoryForPosition = (position: InferredPosition): TLHHoldingCategory => {
    if (position.assetClass === AssetClass.EQUITY && position.assetType === AssetType.ETF) {
        return TLHHoldingCategory.ETF;
    }

    if (
        position.assetClass === AssetClass.EQUITY &&
        position.assetType &&
        STOCK_ASSET_TYPES.includes(position.assetType)
    ) {
        return TLHHoldingCategory.STOCK;
    }

    if (position.assetClass === AssetClass.BOND) {
        return TLHHoldingCategory.BOND;
    }

    return TLHHoldingCategory.OTHER;
};
